import { Button, Popconfirm, Table, Input, Form, Row, Col } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CmsApi, CmsSubjectProductRelations, CmsSubject } from 'apis/cms';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import CmsSubjectProductAddModal from './add-modal';

const CmsSubjectProductRelationListPage = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<CmsSubject>();
  const [dataSource, setDataSource] = useState<CmsSubjectProductRelations[]>(
    [],
  );
  const [productId, setProductId] = useState<number | undefined>(undefined);
  const [form] = Form.useForm();

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CmsApi.getSubjectDetail(Number(id), productId);
      setDetail(data);
      setDataSource(data.cmsSubjectProductRelations);
    } catch (error) {
      console.error('Failed to fetch subject detail:', error);
    } finally {
      setLoading(false);
    }
  }, [id, productId]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleSearch = () => {
    const values = form.getFieldsValue();
    const productIdValue = values.productId
      ? Number(values.productId)
      : undefined;
    setProductId(productIdValue);
  };

  const handleReset = () => {
    form.resetFields();
    setProductId(undefined);
  };

  const columns: ColumnsType<CmsSubjectProductRelations> = useMemo(() => {
    return [
      {
        title: '商品ID',
        dataIndex: 'productId',
        key: 'productId',
        render: (_, record: CmsSubjectProductRelations) => {
          return record?.pmsProduct?.id || '-';
        },
      },
      {
        title: i18n.t('SYLYoWTeQq'),
        dataIndex: 'description',
        key: 'description',
        render: (_, record: CmsSubjectProductRelations) => {
          return record?.pmsProduct?.description || '-';
        },
      },
      {
        title: i18n.t('product_name_1'),
        dataIndex: 'name',
        key: 'name',
        render: (_, record: CmsSubjectProductRelations) => {
          return record?.pmsProduct?.name || '-';
        },
      },

      {
        title: i18n.t('list_remark'),
        dataIndex: 'productSubTitle',
        key: 'productSubTitle',
        render: (_, record: CmsSubjectProductRelations) => {
          return record?.pmsProduct?.note || '-';
        },
      },
      {
        title: i18n.t('publish_status'),
        dataIndex: 'publishStatus',
        key: 'publishStatus',
        render: (_, record: CmsSubjectProductRelations) => {
          const status = record?.pmsProduct?.publishStatus;
          if (status === undefined) return '-';
          return status === 1 ? i18n.t('on_shelf') : i18n.t('off_shelf');
        },
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        fixed: 'right',
        width: 220,
        render: ({ id }: CmsSubjectProductRelations) => {
          return (
            <div>
              <Popconfirm
                title={i18n.t('delete')}
                description={
                  <div className="w-[150px]">
                    {i18n.t('are_you_sure_to_delete')}
                  </div>
                }
                onConfirm={async () => {
                  await CmsApi.subjectProductDelete(id);
                  getList();
                }}
              >
                <Button type="link" danger>
                  <Trans i18nKey={LOCALS.delete} />
                </Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];
  }, [getList]);

  return (
    <div>
      <div className="flex">
        <div className="w-[50%]">
          <Button
            className="mr-4"
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              window.history.back();
            }}
          >
            {i18n.t('back')}
          </Button>
          <span className="font-bold mr-4 text-xl">{detail?.title}</span>
        </div>
        <div className="w-[50%] flex justify-end">
          <Button
            className="mr-4"
            onClick={() => {
              setOpen(true);
            }}
          >
            {i18n.t('add')}
          </Button>
        </div>
      </div>

      {/* 添加筛选表单 */}
      <Form
        form={form}
        layout="inline"
        className="mb-4 mt-4"
        onFinish={handleSearch}
      >
        <Row gutter={24} style={{ width: '100%' }}>
          <Col span={8}>
            <Form.Item name="productId" label="商品ID">
              <Input placeholder="请输入商品ID" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
                style={{ marginRight: 8 }}
              >
                {i18n.t('search') || '搜索'}
              </Button>
              <Button onClick={handleReset}>{i18n.t('reset') || '重置'}</Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        tableLayout="fixed"
        bordered
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={dataSource}
        columns={columns}
      />

      {open && (
        <CmsSubjectProductAddModal
          open={open}
          onClose={() => setOpen(false)}
          data={detail}
          onLoad={getList}
        />
      )}
    </div>
  );
};

export default CmsSubjectProductRelationListPage;
